import { Stack, Typography, styled } from '@mui/material'
import { ICellRendererParams } from 'ag-grid-community'
import { ReactElement } from 'react'

/**
 * Custom ag-grid cell renderer that displays a progress bar with accompanying text.
 *
 * HOW TO USE:
 * -----------
 * This component is designed to work with ag-grid's cellRendererFramework, valueGetter, and valueFormatter.
 *
 * 1. **valueGetter**: Must return a percentage (0-100) that controls the progress bar width
 * 2. **valueFormatter**: Returns the text to display next to the progress bar (e.g., count or percentage)
 *
 * EXAMPLE USAGE:
 * --------------
 * ```typescript
 * const columnDef: ColDef = {
 *   headerName: 'Target Cells Sorted',
 *   field: 'sorted_count',
 *   cellRendererFramework: ProgressCellComponent,
 *   // valueGetter returns percentage for progress bar
 *   valueGetter: (params) => {
 *     const sorted = params.data.sorted_count ?? 0
 *     const total = params.data.total_count ?? 1
 *     return (sorted / total) * 100  // Returns 0-100
 *   },
 *   // valueFormatter returns display text
 *   valueFormatter: (params) => {
 *     return params.data.sorted_count ?? 0  // Returns "1234"
 *   }
 * }
 * ```
 *
 * PARAMETER FLOW:
 * ---------------
 * - ag-grid calls valueGetter → returns percentage → stored in `value` prop
 * - ag-grid calls valueFormatter → returns display text → stored in `valueFormatted` prop
 * - ProgressCellComponent receives both via ICellRendererParams
 *
 * PROPS:
 * ------
 * @param {Partial<ICellRendererParams>} props - ag-grid cell renderer params
 * @param props.value - Percentage (0-100) from valueGetter, used for progress bar width
 * @param props.valueFormatted - Display text from valueFormatter (e.g., "1234" or "67.3%")
 *
 * @returns {ReactElement} A progress bar with text label
 */

interface ProgressbarProps {
  completed: number | string
}

const ProgressContainer = styled('div')({
  height: '9px',
  width: '69px',
  padding: '1px',
  boxSizing: 'border-box',
  backgroundColor: '#EEEFFF',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
})

const ProgressBar = styled('div', {
  shouldForwardProp: (props) => props !== 'completed',
})<ProgressbarProps>(({ completed }) => {
  return {
    height: '100%',
    transition: 'width 3s ease-in-out',
    width: `${completed}%`,
    backgroundColor: '#5F55D1',
    borderRadius: completed === '100.0' ? '10px' : '10px 5px 5px 10px',
  }
})

const ProgressCellComponent = (props: Partial<ICellRendererParams>): ReactElement => {
  const { value, valueFormatted } = props

  // value contains the percentage from valueGetter for the progress bar
  const progress: string = parseFloat(value?.toString() ?? '0').toFixed(1)

  // valueFormatted contains the formatted display text (count or percentage)
  const displayText = valueFormatted ?? `${progress}%`

  return (
    <Stack gap="15px" direction="row" alignItems="center">
      <ProgressContainer data-testid="progress-container">
        <ProgressBar data-testid="progress-bar" completed={progress} />
      </ProgressContainer>
      <Typography sx={{ fontSize: '12px' }}>{displayText}</Typography>
    </Stack>
  )
}

export default ProgressCellComponent
